






















































import { Component, Prop, Vue } from 'vue-property-decorator';

import { MmAuditor } from '../types/domain';

/** Сентинел «Без аудитора» наружу не выходит: настоящие идентификаторы — строки, число им не равно */
const CLEAR_VALUE = -1;

const CLEAR_OPTION_KEY = '__mm_auditor_clear__';

@Component
export default class MmAuditorSelect extends Vue {
	@Prop({ required: true }) readonly options!: MmAuditor[];

	/** Тип допускает и строку: боевой id — UUID, заглушка отдаёт число; сравнение через String() */
	@Prop({ default: null }) readonly value!: number | string | null;

	@Prop({ default: false }) readonly disabled!: boolean;

	@Prop({ default: '' }) readonly disabledReason!: string;

	@Prop({ default: false }) readonly loading!: boolean;

	@Prop({ default: true }) readonly allowClear!: boolean;

	@Prop({ default: '' }) readonly placeholder!: string;

	@Prop({ default: 'default' }) readonly size!: string;

	@Prop({ default: false }) readonly autofocus!: boolean;

	CLEAR_VALUE = CLEAR_VALUE;
	CLEAR_OPTION_KEY = CLEAR_OPTION_KEY;

	isDropdownOpen: boolean = false;

	get safeOptions(): MmAuditor[] {
		if (!this.options || this.options.length === 0) {
			return [];
		}

		return this.options.filter((item) => item != null && item.id != null && item.login != null);
	}

	get optionsById(): { [key: string]: MmAuditor } {
		const index: { [key: string]: MmAuditor } = {};

		this.safeOptions.forEach((item) => {
			index[String(item.id)] = item;
		});

		return index;
	}

	/** Строка и undefined вместо null: ant сравнивает строго и вшивает тип в ключ опции */
	get innerValue(): string | undefined {
		if (this.value == null) {
			return undefined;
		}

		const key = String(this.value);

		if (!this.optionsById[key]) {
			return undefined;
		}

		return key;
	}

	get placeholderText(): string {
		if (this.placeholder !== '') {
			return this.placeholder;
		}

		return this.$t('priceMonitoring.mobileMonitoring.assignments.auditorSelectPlaceholder') as string;
	}

	get clearLabel(): string {
		return this.$t('priceMonitoring.mobileMonitoring.assignments.auditorClearOption') as string;
	}

	get tooltipText(): string {
		if (!this.disabled) {
			return '';
		}

		return this.disabledReason ? String(this.disabledReason).trim() : '';
	}

	/** Фокус в nextTick: внутреннее поле a-select появляется в DOM позже нашего mounted */
	mounted(): void {
		if (!this.autofocus) {
			return;
		}

		this.$nextTick(() => {
			this.focus();
		});
	}

	focus(): void {
		const select = this.$refs.select as any;

		if (select && typeof select.focus === 'function') {
			select.focus();
		}
	}

	readFullName(auditor: MmAuditor): string {
		if (!auditor || !auditor.fullName) {
			return '';
		}

		return String(auditor.fullName).trim();
	}

	optionValue(auditor: MmAuditor): string {
		return auditor && auditor.id != null ? String(auditor.id) : '';
	}

	optionTitle(auditor: MmAuditor): string {
		const login = auditor && auditor.login ? String(auditor.login) : '';
		const fullName = this.readFullName(auditor);

		return fullName !== '' ? login + ' — ' + fullName : login;
	}

	/** ant зовёт эту функцию и игнорирует optionFilterProp, а на пустом запросе не зовёт вовсе */
	filterOption(input: string, option: any): boolean {
		const query = input == null ? '' : String(input).trim().toLowerCase();

		if (query === '') {
			return true;
		}

		const value = this.readOptionValue(option);

		// Значение опции — строка, а сентинел число, поэтому сравниваем со строковой записью
		if (value === null || value === String(CLEAR_VALUE)) {
			return false;
		}

		const auditor = this.optionsById[value];

		if (!auditor) {
			return false;
		}

		const login = auditor.login ? String(auditor.login).toLowerCase() : '';
		const fullName = auditor.fullName ? String(auditor.fullName).toLowerCase() : '';

		return login.indexOf(query) >= 0 || fullName.indexOf(query) >= 0;
	}

	/** Значение уходит как есть: идентификатор аудитора — строка, приведение к числу дало бы NaN */
	handleChange(selected: any): void {
		if (selected == null || selected === CLEAR_VALUE) {
			this.$emit('change', null);

			return;
		}

		this.$emit('change', selected);
	}

	handleDropdownVisibleChange(open: boolean): void {
		this.isDropdownOpen = open === true;
	}

	/** Стрелкам при закрытом списке нужен stopPropagation, иначе vc-select откроет выпадающий список */
	handleKeydown(event: KeyboardEvent): void {
		const key = this.readKeyName(event);

		if (key === 'Escape') {
			this.$emit('cancel');

			return;
		}

		if (this.isDropdownOpen) {
			return;
		}

		if (key === 'Enter') {
			event.preventDefault();
			this.$emit('confirm');

			return;
		}

		if (key === 'ArrowUp' || key === 'ArrowDown') {
			event.preventDefault();
			event.stopPropagation();
			this.$emit('keydown-nav', key === 'ArrowUp' ? 'up' : 'down');
		}
	}

	/** Старые движки дают устаревшие имена клавиш или не дают key вовсе — отсюда путь через keyCode */
	private readKeyName(event: KeyboardEvent): string {
		const key = event && event.key ? String(event.key) : '';

		if (key !== '') {
			if (key === 'Esc') {
				return 'Escape';
			}

			if (key === 'Up') {
				return 'ArrowUp';
			}

			if (key === 'Down') {
				return 'ArrowDown';
			}

			return key;
		}

		const code = event ? event.keyCode : 0;

		if (code === 27) {
			return 'Escape';
		}

		if (code === 13) {
			return 'Enter';
		}

		if (code === 38) {
			return 'ArrowUp';
		}

		if (code === 40) {
			return 'ArrowDown';
		}

		return '';
	}

	/** Пропы опции доступны только через componentOptions.propsData */
	private readOptionValue(option: any): string | null {
		if (!option || !option.componentOptions || !option.componentOptions.propsData) {
			return null;
		}

		const raw = option.componentOptions.propsData.value;

		if (raw == null) {
			return null;
		}

		return String(raw);
	}
}
